import React, { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";

const ACTION_LABEL = {
  "user.update": "Updated member",
  "reference.create": "Added reference value",
  "reference.update": "Edited reference value",
  "reference.deactivate": "Deactivated reference value",
  "reference.delete": "Removed reference value",
};

export default function AdminAudit() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/admin/audit", { params: { page, page_size: 25 } }); setData(data); }
    catch { setData({ entries: [], total: 0 }); }
  }, [page]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loading />;
  const totalPages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div data-testid="admin-audit">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-[640] text-foreground">Audit log</h2>
        <AdminRefreshButton testId="admin-refresh-audit" onRefresh={load} />
      </div>
      {data.total === 0 ? (
        <EmptyState icon={ScrollText} title="No admin activity yet" description="Every change an admin makes will be recorded here." testId="admin-audit-empty" />
      ) : (
        <>
          <Card className="border-0 p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.entries.map((e) => (
                    <TableRow key={e.id} data-testid={`admin-audit-${e.id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatDistanceToNow(parseISO(e.created_at), { addSuffix: true })}</TableCell>
                      <TableCell className="text-sm"><div className="font-[540] text-foreground">{e.admin_name || "Admin"}</div><div className="text-xs text-muted-foreground">{e.admin_email}</div></TableCell>
                      <TableCell><Badge variant="secondary">{ACTION_LABEL[e.action] || e.action}</Badge></TableCell>
                      <TableCell className="text-sm"><span className="capitalize text-foreground">{e.target_type}</span> <span className="font-mono text-xs text-muted-foreground">{e.target_id}</span></TableCell>
                      <TableCell className="max-w-xs truncate font-mono text-xs text-muted-foreground">{e.meta && Object.keys(e.meta).length ? JSON.stringify(e.meta) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{data.total} entries</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-audit-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
              <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-audit-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
